import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

let interceptorsRegistered = false;
let refreshPromise = null;

const getStoredToken = () => {
	const raw = localStorage.getItem("token");
	if (!raw) return null;
	try {
		return JSON.parse(raw);
	} catch {
		return raw;
	}
};

const registerAuthInterceptors = ({
	onAuth,
	onLogout,
}) => {
	if (interceptorsRegistered) return;
	interceptorsRegistered = true;

	api.interceptors.request.use(
		config => {
			const token = getStoredToken();
			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
			}
			return config;
		},
		error => Promise.reject(error)
	);

	api.interceptors.response.use(
		response => response,
		async error => {
			const originalRequest = error.config || {};
			const status = error?.response?.status;
			const url = originalRequest.url || "";

			if (url.includes("/auth/refresh_token") || url.includes("/auth/login")) {
				return Promise.reject(error);
			}

			if (status === 403 && !originalRequest._retry) {
				originalRequest._retry = true;

				try {
					if (!refreshPromise) {
						refreshPromise = api
							.post("/auth/refresh_token")
							.then(({ data }) => {
								if (data?.token) {
									localStorage.setItem("token", JSON.stringify(data.token));
									api.defaults.headers.Authorization = `Bearer ${data.token}`;
									if (onAuth) onAuth(data);
								}
								return data;
							})
							.finally(() => {
								refreshPromise = null;
							});
					}

					await refreshPromise;
					originalRequest.headers = originalRequest.headers || {};
					originalRequest.headers.Authorization = `Bearer ${getStoredToken()}`;
					return api(originalRequest);
				} catch (refreshError) {
					localStorage.removeItem("token");
					api.defaults.headers.Authorization = undefined;
					if (onLogout) onLogout();
					return Promise.reject(refreshError);
				}
			}

			if (status === 401) {
				localStorage.removeItem("token");
				api.defaults.headers.Authorization = undefined;
				if (onLogout) onLogout();
			}

			return Promise.reject(error);
		}
	);
};

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});
	const userIdRef = useRef(null);

	useEffect(() => {
		registerAuthInterceptors({
			onAuth: data => {
				setIsAuth(true);
				if (data?.user) setUser(data.user);
			},
			onLogout: () => {
				setIsAuth(false);
				setUser({});
			},
		});
	}, []);

	useEffect(() => {
		const token = localStorage.getItem("token");
		(async () => {
			if (token) {
				try {
					const { data } = await api.post("/auth/refresh_token");
					localStorage.setItem("token", JSON.stringify(data.token));
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					setIsAuth(true);
					setUser(data.user);
				} catch (err) {
					localStorage.removeItem("token");
					api.defaults.headers.Authorization = undefined;
					setIsAuth(false);
				}
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		userIdRef.current = user?.id;
	}, [user]);

	useEffect(() => {
		if (!isAuth) return undefined;

		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === userIdRef.current) {
				setUser(data.user);
			}
		});

		return () => {
			socket.off("user");
		};
	}, [isAuth]);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			localStorage.setItem("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push("/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
